import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { CircularProgress, Box } from '@mui/material';

interface AdminOwnerRouteProps {
  children: React.ReactNode;
}

export const AdminOwnerRoute: React.FC<AdminOwnerRouteProps> = ({ children }) => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Get the current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;

        if (!session) {
          setIsAuthorized(false);
          return;
        }

        // Get the user's profile from the profiles table
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', session.user.id)
          .single();

        if (profileError) throw profileError;

        // Check if user has admin or owner role
        const hasRequiredRole = profile?.role === 'admin' || profile?.role === 'owner';
        setIsAuthorized(hasRequiredRole);
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthorized(false);
      }
    };

    checkAuth();
  }, []);

  // Show loading state while checking authorization
  if (isAuthorized === null) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authorized
  if (!isAuthorized) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render children if authorized
  return <>{children}</>;
}; 